import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Tabs, Input, Alert, Dropdown, message, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import styled, { createGlobalStyle } from 'styled-components';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import ConfirmModal from '../../../components/common/ConfirmModal';
import AddItemButton from '../../../components/common/AddItemButton';
import FinancialStatementItemDrawer, {
  LineItemFormData,
} from './FinancialStatementItemDrawer';
import {
  createLineItem,
  CreateLineItemRequest,
  getAssets,
  getLiabilities,
  getEquities,
  getRevenues,
  getExpenses,
  parseBackendLineItem,
  updateLineItem,
  UpdateLineItemRequest,
  deleteLineItem,
  saveMaterialityThreshold as saveMaterialityThresholdAPI,
} from '../../../services/financialStatementService';
import { CycleItem, ProcessItem } from '../../../services/hierarchyService';
import { RootState, AppDispatch } from '../../../store';
import { setAllFinancialData, clearFinancialData } from '../../../store/slices/financialStatementSlice';
import InfoIcon from '../../../assets/icons/ic-info.svg?react';
import ArrowUpIcon from '../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../assets/icons/ic-arrow-down.svg?react';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../assets/icons/ic-trash.svg?react';
import warningIcon from '../../../assets/icons/ic-warning.svg';
import { setMateriality } from '@/store/slices/annualReportSlice';
import ExportPanel, { ExportColumnConfig } from '../../../components/common/ExportPanel';

const { Title } = Typography;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const ContentWrapper = styled.div`
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    .ant-tabs-tab-btn {
      color: #6B7280;
      
      svg {
        transition: color 0.3s;
        
        path {
          transition: stroke 0.3s;
        }
      }
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
        
        svg {
          path {
            stroke: #2563EB;
          }
        }
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
        
        svg {
          path {
            stroke: #2563EB;
          }
        }
      }
    }
  }
`;

const MaterialityThresholdSection = styled.div`
  margin-bottom: 24px;
`;

const ThresholdLabel = styled.div`
  display: flex;
  color: #1F2937;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 16px;
  
  svg {
    display: inline-flex;
    cursor: help;
    
    &:hover {
      opacity: 0.7;
    }
  }
`;

const ThresholdInput = styled(Input)`
  width: 300px;
  
  /* Remove all number input spinners/arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  
  /* Additional override for Ant Design Input wrapper */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const ErrorBanner = styled(Alert)`
    margin-bottom: 16px;
  height: 52px;
  display: flex;
  align-items: center;
  background: #FEF9C3 !important;
  border: 1px solid #FEF08A !important;
  border-radius: 6px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);

  .ant-alert-message {
    font-family: Inter;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0.005em;
    color: #854D0E;
  }

  .ant-alert-icon {
    color: #854D0E;
  }
`;

const ReconciliationErrorBanner = styled(Alert)`
  margin-bottom: 16px;
  height: 52px;
  display: flex;
  align-items: center;
  background: #FEE2E2 !important;
  border: 1px solid #FECACA !important;
  border-radius: 6px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);

  .ant-alert-message {
    font-family: Inter;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0.005em;
    color: #991B1B;
  }

  .ant-alert-close-icon {
    color: #854D0E;
    
    svg {
      font-size: 14px;
    }
  }
`;

const AccountSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 24px;
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const AccountTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #1F2937;
  margin-bottom: 16px;
  margin-left: 16px;
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .e-grid {
  border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.005em;
    color: #6B7280;
    text-transform: uppercase;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    font-size: 14px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state when clicking action button */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const EmptyStateWrapper = styled.div`
    text-align: center;
  padding: 60px 20px;
  background: white;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: grid;
  grid-template-columns: 300px auto;
  align-items: center;
  background: white;
  border-radius: 0 0 6px 6px;
  border-top: 2px solid #E5E7EB;
`;

const TotalLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  padding: 16px;
  border-right: 1px solid #E5E7EB;
`;

const TotalAmount = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #1F2937;
  padding: 16px;
`;


const NetIncomeRow = styled.div`
  display: grid;
  grid-template-columns: 300px auto;
  align-items: center;
  background: white;
  border-radius: 0 0 6px 6px;
  border: 2px solid #E5E7EB;
`;

const NetIncomeLabel = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  padding: 16px;
  border-right: 1px solid #E5E7EB;
`;

const NetIncomeValue = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #1F2937;
  padding: 16px;
`;

// Tag styling for Cycle and Process columns
const CycleTag = styled.span`
    display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #ECFDF5;
  color: #115E59;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ProcessTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ImmaterialTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #F3F4F6;
  color: #1F2937;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
`;

// Tooltip content styling
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  white-space: normal;
  word-break: break-word;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const SetProcessButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #EF4444;
  color: #EF4444;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  height: 32px;

  &:hover {
    background: #FEF2F2;
  }

  &:focus {
    outline: none;
  }
`;

// Global styles for dropdown menu and tooltip
const DropdownMenuStyles = createGlobalStyle`
  .financial-statement-dropdown {
    .ant-dropdown-menu {
      width: 200px;
      background: white;
      border-radius: 12px;
      padding: 8px;
      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
    }

    .ant-dropdown-menu-item {
      padding: 10px 12px;
      border-radius: 6px;
      
      &:hover {
        background: #F3F4F6;
      }
    }
  }

  /* Custom tooltip styling for materiality threshold */
  .ant-tooltip {
    .ant-tooltip-arrow {
      &::before {
        background: #FFFFFF !important;
      }
    }
    
    .ant-tooltip-inner {
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TabsContainer = styled.div`
  position: relative;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

interface AccountSectionProps {
  title: string;
  bgColor: string;
  data: any[];
  onAddItem: () => void;
  onEditItem: (item: any) => void;
  onDeleteItem: (item: any) => void;
  onAddRowAbove: (item: any) => void;
  onAddRowBelow: (item: any) => void;
}

/**
 * Helper function to parse amount string to number
 * Handles both positive ($1,000) and negative ($(1,000)) formats
 */
const parseAmountString = (amountStr: string | number): number => {
  if (typeof amountStr === 'number') {
    return amountStr;
  }
  
  // Handle negative format: $(1000) => -1000
  if (amountStr.startsWith('$(') && amountStr.endsWith(')')) {
    const numStr = amountStr.slice(2, -1).replace(/,/g, '');
    return -parseFloat(numStr);
  }
  
  // Handle positive format: $1000 => 1000
  return parseFloat(amountStr.replace(/[$,]/g, ''));
};

const AccountSectionComponent: React.FC<AccountSectionProps> = ({
  title,
  bgColor,
  data,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onAddRowAbove,
  onAddRowBelow,
}) => {
  const navigate = useNavigate();
  const hasData = data && data.length > 0;
  const { isAdmin } = useSelector((state: RootState) => state.auth);

  // Calculate total amount
  const calculateTotal = () => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => {
      const amount = parseAmountString(item.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  };

  const totalAmount = calculateTotal();
  
  // Format total: positive as $1,000, negative as $(1,000)
  const formattedTotal = totalAmount < 0
    ? `$(${Math.abs(totalAmount).toLocaleString()})`
    : `$${totalAmount.toLocaleString()}`;

  // Handle click on cycle/process tag
  const handleCycleClick = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleProcessClick = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  // Template for Cycle column
  const cycleTemplate = (props: any) => {
    // Show Immaterial tag if line item is marked as immaterial
    if (props.isImmaterial) {
      return <ImmaterialTag>Immaterial</ImmaterialTag>;
    }

    if (!props.cycles || props.cycles.length === 0) {
      return (
        <SetProcessButton onClick={() => onEditItem(props)}>
          Set Cycle <span>›</span>
        </SetProcessButton>
      );
    }

    // Tooltip content for multiple cycles
    const tooltipContent = props.cycles.length > 1 ? (
      <div>
        <TooltipTitle>Cycles</TooltipTitle>
        <TooltipList>
          {props.cycles.map((cycle: CycleItem, index: number) => (
            <TooltipItem
              key={index}
              onClick={() => handleCycleClick(cycle.cycleId)}
            >
              {`${cycle.cycleCode} ${cycle.cycleName}`}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const cycleContent = (
      <div>
        {props.cycles.map((cycle: CycleItem, index: number) => (
          <CycleTag
            key={index}
            onClick={() => handleCycleClick(cycle.cycleId)}
          >
            {`${cycle.cycleCode} ${cycle.cycleName}`}
          </CycleTag>
        ))}
      </div>
    );

    // Show tooltip only if multiple cycles
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {cycleContent}
        </Tooltip>
      );
    }

    return cycleContent;
  };

  // Template for Process column
  const processTemplate = (props: any) => {
    // Show Immaterial tag if line item is marked as immaterial
    if (props.isImmaterial) {
      return <ImmaterialTag>Immaterial</ImmaterialTag>;
    }

    if (!props.processes || props.processes.length === 0) {
      return (
        <SetProcessButton onClick={() => onEditItem(props)}>
          Set Process <span style={{ fontSize: '20px' }}>›</span>
        </SetProcessButton>
      );
    }

    // Tooltip content for multiple processes
    const tooltipContent = props.processes.length > 1 ? (
      <div>
        <TooltipTitle>Processes</TooltipTitle>
        <TooltipList>
          {props.processes.map((process: ProcessItem, index: number) => (
            <TooltipItem
              key={index}
              onClick={() => handleProcessClick(process.processId)}
            >
              {`${process.processCode} ${process.processName}`}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const processContent = (
      <div>
        {props.processes.map((process: ProcessItem, index: number) => (
          <ProcessTag
            key={index}
            onClick={() => handleProcessClick(process.processId)}
          >
            {`${process.processCode} ${process.processName}`}
          </ProcessTag>
        ))}
      </div>
    );

    // Show tooltip only if multiple processes
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {processContent}
        </Tooltip>
      );
    }

    return processContent;
  };

  // Template for Action column
  const actionTemplate = (props: any) => {
    const getMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onAddRowAbove(props),
      },
      {
        key: 'add-below',
        label: (
          <MenuItemContent>
            <ArrowDownIcon />
            <MenuItemText>Add row below</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onAddRowBelow(props),
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit row</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onEditItem(props),
      },
      {
        key: 'delete',
        label: (
          <MenuItemContent>
            <TrashIcon />
            <MenuItemText $danger>Delete</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onDeleteItem(props),
      },
    ];

    // Prevent click event from propagating to row (prevents active state)
    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div onClick={handleActionButtonClick}>
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="financial-statement-dropdown"
        >
          <ActionButton>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      </div>
    );
  };

  return (
    <AccountSection $bgColor={bgColor}>
      <AccountTitle>{title}</AccountTitle>
      <GridWrapper>
        <GridComponent
          dataSource={data}
          allowPaging={false}
          enableHover={true}
        >
          <ColumnsDirective>
            <ColumnDirective
              field='name'
              headerText='Line Item'
              width='300'
              textAlign='Left'
            />
            <ColumnDirective
              field='amount'
              headerText='Amount'
              width='150'
              textAlign='Left'
            />
            <ColumnDirective
              field='cycles'
              headerText='Cycle'
              width='200'
              textAlign='Left'
              template={cycleTemplate}
            />
            <ColumnDirective
              field='processes'
              headerText='Process'
              width='auto'
              textAlign='Left'
              template={processTemplate}
            />
            <ColumnDirective
              headerText='Action'
              width='80'
              textAlign='Center'
              template={actionTemplate}
              visible={isAdmin}
            />
          </ColumnsDirective>
          <Inject services={[Page]} />
        </GridComponent>

        {hasData && (
          <TotalRow>
            <TotalLabel>Total {title}</TotalLabel>
            <TotalAmount>{formattedTotal}</TotalAmount>
          </TotalRow>
        )}
      </GridWrapper>

      {!hasData && (
        <EmptyStateWrapper>
          <EmptyStateText>There are no records to display yet.</EmptyStateText>
          <AddItemButton onClick={onAddItem} text="Add Item" disabled={!isAdmin} />
        </EmptyStateWrapper>
      )}
    </AccountSection>
  );
};

const FinancialStatementsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get selectedReport from Redux to access annualReportId
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const materiality = selectedReport?.materiality;
  const financialStatementId = selectedReport?.financialStatementId;
  const materialityTooltipText = selectedReport?.materialityDescription
    || 'Reference value only. This number will not affect calculations or alerts in this version.';

  // Get financial statement data from Redux
  const {
    assetsData,
    liabilitiesData,
    equityData,
    revenueData,
    expensesData,
    lastFetchedReportId,
    isFetched,
  } = useSelector((state: RootState) => state.financialStatement);

  const [activeTab, setActiveTab] = useState('balance-sheet');
  const [hasInvalidLineItems, setHasInvalidLineItems] = useState(false);
  const [hasReconciliationError, setHasReconciliationError] = useState(false);
  const [isReconciliationBannerDismissed, setIsReconciliationBannerDismissed] = useState(false);

  // Single materiality threshold shared between both statements
  const [materialityThreshold, setMaterialityThreshold] = useState('');

  // Ref for debouncing autosave
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drawer state - simplified
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedItem, setSelectedItem] = useState<Partial<LineItemFormData> | undefined>();
  const [currentSection, setCurrentSection] = useState<string>('');
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null); // For insert above/below

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Change Log drawer state

  // Loading states
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Export panel state
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  
  // Track if user is actively editing materiality (to prevent Redux sync overwriting input)
  const isEditingMaterialityRef = useRef(false);
  const lastSyncedMaterialityRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Only sync from Redux if:
    // 1. User is not actively editing
    // 2. The value actually changed from external source (not our own save)
    if (isEditingMaterialityRef.current) {
      return;
    }
    
    // Skip if this is the same value we just saved
    if (materiality === lastSyncedMaterialityRef.current) {
      return;
    }
    
    lastSyncedMaterialityRef.current = materiality;
    
    // Format with commas when loading from Redux
    const rawValue = materiality?.toString() || '';
    if (rawValue) {
      const parts = rawValue.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      setMaterialityThreshold(parts.join('.'));
    } else {
      setMaterialityThreshold('');
    }
  }, [materiality]);

  // Fetch line items data when selectedReport changes
  useEffect(() => {
    const fetchLineItems = async () => {
      if (!financialStatementId) {
        // Clear data if no report selected
        dispatch(clearFinancialData());
        return;
      }

      // If data is already fetched for this report, skip fetching
      if (isFetched && lastFetchedReportId === financialStatementId) {
        return;
      }
      // console.log('fetchLineItems', financialStatementId);
      setIsLoadingData(true);

      const request = {
        requiresCounts: true,
        skip: 0,
        take: 1000, // Get all items (adjust if needed)
      };

      // Fetch all sections in parallel with Promise.allSettled
      // This ensures one failure doesn't block all others
      const results = await Promise.allSettled([
        getAssets(financialStatementId, request),
        getLiabilities(financialStatementId, request),
        getEquities(financialStatementId, request),
        getRevenues(financialStatementId, request),
        getExpenses(financialStatementId, request),
      ]);

      // Track failed sections
      const failedSections: string[] = [];

      // Process each result independently
      const [assetsResult, liabilitiesResult, equitiesResult, revenuesResult, expensesResult] = results;

      let fetchedAssetsData: any[] = [];
      let fetchedLiabilitiesData: any[] = [];
      let fetchedEquityData: any[] = [];
      let fetchedRevenueData: any[] = [];
      let fetchedExpensesData: any[] = [];

      // Assets
      if (assetsResult.status === 'fulfilled') {
        fetchedAssetsData = assetsResult.value.result.map(parseBackendLineItem);
      } else {
        console.error('Failed to fetch assets:', assetsResult.reason);
        failedSections.push('Assets');
      }

      // Liabilities
      if (liabilitiesResult.status === 'fulfilled') {
        fetchedLiabilitiesData = liabilitiesResult.value.result.map(parseBackendLineItem);
      } else {
        console.error('Failed to fetch liabilities:', liabilitiesResult.reason);
        failedSections.push('Liabilities');
      }

      // Equity
      if (equitiesResult.status === 'fulfilled') {
        fetchedEquityData = equitiesResult.value.result.map(parseBackendLineItem);
      } else {
        console.error('Failed to fetch equity:', equitiesResult.reason);
        failedSections.push('Equity');
      }

      // Revenue
      if (revenuesResult.status === 'fulfilled') {
        fetchedRevenueData = revenuesResult.value.result.map(parseBackendLineItem);
      } else {
        console.error('Failed to fetch revenue:', revenuesResult.reason);
        failedSections.push('Revenue');
      }

      // Expenses
      if (expensesResult.status === 'fulfilled') {
        fetchedExpensesData = expensesResult.value.result.map(parseBackendLineItem);
      } else {
        console.error('Failed to fetch expenses:', expensesResult.reason);
        failedSections.push('Expenses');
      }

      // Save all data to Redux
      dispatch(setAllFinancialData({
        assetsData: fetchedAssetsData,
        liabilitiesData: fetchedLiabilitiesData,
        equityData: fetchedEquityData,
        revenueData: fetchedRevenueData,
        expensesData: fetchedExpensesData,
        reportId: financialStatementId,
      }));

      // Show appropriate message
      if (failedSections.length === 5) {
        // All failed
        message.error('Failed to load all financial statement data');
      } else if (failedSections.length > 0) {
        // Partial failure
        message.warning(`Failed to load: ${failedSections.join(', ')}`);
      }

      setIsLoadingData(false);
    };

    fetchLineItems();
  }, [financialStatementId, isFetched, lastFetchedReportId, dispatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  // Validate all line items
  const validateLineItems = () => {
    // Combine all line items from all sections
    const allLineItems = [
      ...assetsData,
      ...liabilitiesData,
      ...equityData,
      ...revenueData,
      ...expensesData,
    ];

    // Check if any line item is invalid
    // Invalid: NOT immaterial AND has Cycle BUT lacks Process
    const hasInvalid = allLineItems.some(item => {
      const isImmaterial = item.isImmaterial === true;
      const hasCycles = Array.isArray(item.cycles) && item.cycles.length > 0;
      const hasProcesses = Array.isArray(item.processes) && item.processes.length > 0;

      // Invalid if: NOT immaterial AND has Cycle BUT no Process
      return !isImmaterial && hasCycles && !hasProcesses;
    });

    setHasInvalidLineItems(hasInvalid);
  };

  // Run validation whenever line item data changes
  useEffect(() => {
    validateLineItems();
  }, [assetsData, liabilitiesData, equityData, revenueData, expensesData]);

  // Calculate total amount for a section
  const calculateSectionTotal = (data: any[]): number => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => {
      const amount = parseAmountString(item.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  };

  // Reconciliation validation for Balance Sheet
  const validateReconciliation = () => {
    // Only validate when Balance Sheet tab is active
    if (activeTab !== 'balance-sheet') {
      setHasReconciliationError(false);
      return;
    }

    // Calculate totals
    const totalAssets = calculateSectionTotal(assetsData);
    const totalLiabilities = calculateSectionTotal(liabilitiesData);
    const totalEquity = calculateSectionTotal(equityData);

    // Check reconciliation: Total Assets - Total Liabilities = Total Equity
    // Use a small epsilon for floating point comparison (0.01 = 1 cent tolerance)
    const calculatedEquity = totalAssets - totalLiabilities;
    const difference = Math.abs(calculatedEquity - totalEquity);
    const isReconciled = difference < 0.01;

    // Set error state
    setHasReconciliationError(!isReconciled);
    
    // Reset dismissed state when data changes (so banner shows again if still invalid)
    if (!isReconciled) {
      setIsReconciliationBannerDismissed(false);
    }
  };

  // Run reconciliation check whenever relevant data changes or tab changes
  useEffect(() => {
    validateReconciliation();
  }, [assetsData, liabilitiesData, equityData, activeTab]);

  // Autosave materiality threshold (debounced)
  const saveMaterialityThreshold = async (value: string) => {
    try {
      // Ensure we have required data
      if (!financialStatementId) {
        console.error('Financial statement ID not found');
        return;
      }
      // Parse string to number
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        console.error('Invalid materiality threshold value');
        return;
      }
      // Call API
      await saveMaterialityThresholdAPI(financialStatementId, numValue);
      dispatch(setMateriality(numValue));
      // console.log('Materiality threshold saved successfully:', numValue);
    } catch (error) {
      console.error('Failed to save materiality threshold:', error);
      message.error('Failed to save materiality threshold');
    }
  };

  // Validate materiality input (positive numbers and decimals only)
  const validateMaterialityInput = (value: string): boolean => {
    if (value === '') return true; // Empty is allowed
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue >= 0;
  };

  // Format number with commas (e.g., 12345.9 -> 12,345.9)
  // Preserves trailing dot for user input (e.g., "123." stays as "123.")
  const formatNumberWithCommas = (value: string, preserveTrailingDot = false): string => {
    if (!value) return '';
    
    const hasTrailingDot = value.endsWith('.');
    
    // Split by decimal point
    const parts = value.split('.');
    // Format the integer part with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Handle trailing dot
    if (hasTrailingDot && preserveTrailingDot && parts.length === 2 && parts[1] === '') {
      return parts[0] + '.';
    }
    
    // Join back with decimal part if exists
    return parts.length > 1 ? parts.join('.') : parts[0];
  };

  // Remove commas from formatted number
  const removeCommas = (value: string): string => {
    return value.replace(/,/g, '');
  };

  // Handle materiality change with debounced autosave
  const handleMaterialityChange = (value: string) => {
    // Mark as editing to prevent Redux sync from overwriting
    isEditingMaterialityRef.current = true;
    
    // Remove commas first to get raw value
    const rawValue = removeCommas(value);
    
    // Filter out any non-numeric characters except dots
    // Allow: digits (0-9), dot (.)
    const filteredValue = rawValue.replace(/[^0-9.]/g, '');
    
    // Validate input (but allow trailing dot for user typing)
    const valueToValidate = filteredValue.endsWith('.') ? filteredValue.slice(0, -1) : filteredValue;
    if (valueToValidate !== '' && !validateMaterialityInput(valueToValidate)) {
      return;
    }
    
    // Format with commas for display, preserve trailing dot
    const formattedValue = formatNumberWithCommas(filteredValue, true);
    
    // Update state with formatted value
    setMaterialityThreshold(formattedValue);
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Set new timer for autosave (1500ms debounce - longer delay for better UX)
    saveTimerRef.current = setTimeout(() => {
      // Only save if there's a valid number (not empty, not just a dot)
      const valueToSave = filteredValue.endsWith('.') ? filteredValue.slice(0, -1) : filteredValue;
      if (valueToSave !== '') {
        saveMaterialityThreshold(valueToSave);
        lastSyncedMaterialityRef.current = parseFloat(valueToSave);
      }
      // Allow Redux sync again after save
      isEditingMaterialityRef.current = false;
    }, 1500);
  };

  // Handle key press to prevent typing invalid characters
  const handleMaterialityKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    
    // Allow: Backspace, Tab, Enter, Arrow keys, Delete
    if (['Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(key)) {
      return;
    }
    
    // Allow: digits (0-9)
    if (/^[0-9]$/.test(key)) {
      return;
    }
    
    // Allow: dot (.) for decimal separator
    if (key === '.') {
      // Only allow one decimal separator
      const currentValue = removeCommas((e.target as HTMLInputElement).value);
      if (currentValue.includes('.')) {
        e.preventDefault();
        return;
      }
      return;
    }
    
    // Prevent all other characters
    e.preventDefault();
  };

  const handleDismissReconciliationBanner = () => {
    setIsReconciliationBannerDismissed(true);
  };

  const handleAddItem = (section: string) => {
    setCurrentSection(section);
    setDrawerMode('add');
    setSelectedItem(undefined);
    setInsertAtIndex(null); // Reset insert position
    setDrawerOpen(true);
  };

  const handleEditItem = (item: any, section: string) => {
    setCurrentSection(section);
    setDrawerMode('edit');
    
    setSelectedItem({
      id: item.id,
      name: item.name,
      amount: parseAmountString(item.amount),
      isImmaterial: item.isImmaterial || false,
      cycles: item.cycles || [],
      processes: item.processes || [],
    });
    setDrawerOpen(true);
  };

  const handleDeleteItem = (item: any, section: string) => {
    setCurrentSection(section);
    setSelectedItem(item)
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    setIsDeleting(true);
    try {
      // Call API
      await deleteLineItem(selectedItem.id);
      // Show success toast notification
      message.success('Line item deleted successfully');
      // Close modal and reset state
      setShowDeleteModal(false);
      reloadSelectedSection();
    } catch (error) {
      message.error('Failed to delete line item');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedItem(undefined);
  };

  const handleAddRowAbove = (item: any, section: string) => {
    setCurrentSection(section);
    setDrawerMode('add');
    setSelectedItem(undefined);
    setInsertAtIndex(item.index); // Insert at the same index (pushes current item down)
    setDrawerOpen(true);
  };

  const handleAddRowBelow = (item: any, section: string) => {
    setCurrentSection(section);
    setDrawerMode('add');
    setSelectedItem(undefined);
    setInsertAtIndex(item.index + 1); // Insert after the current item
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(undefined);
    setInsertAtIndex(null); // Reset insert position
  };

  const handleSubmitForm = async (values: LineItemFormData) => {
    // Ensure we have required data
    if (!financialStatementId) {
      message.error('Financial statement ID not found');
      return;
    }
    // Map section to line item type
    const lineItemTypeMap: Record<string, 'asset' | 'liability' | 'equity' | 'revenue' | 'expenses'> = {
      'assets': 'asset',
      'liabilities': 'liability',
      'equity': 'equity',
      'revenue': 'revenue',
      'expenses': 'expenses',
    };
    const lineItemType = lineItemTypeMap[currentSection];

    // Get current data to calculate index
    const getCurrentData = () => {
      switch (currentSection) {
        case 'assets': return assetsData;
        case 'liabilities': return liabilitiesData;
        case 'equity': return equityData;
        case 'revenue': return revenueData;
        case 'expenses': return expensesData;
        default: return [];
      }
    };
    const currentData = getCurrentData();
    try {
      if (drawerMode === 'add') {
        // Use insertAtIndex if specified (for add above/below), otherwise append at the end
        const index = insertAtIndex !== null ? insertAtIndex : currentData.length;
        // Prepare API request
        const createRequest: CreateLineItemRequest = {
          financialStatementId,
          index,
          name: values.name,
          amount: values.amount,
          isImmaterial: values.isImmaterial,
          cycleIds: values.cycles.map(cycle => cycle.cycleId),
          processIds: values.processes.map(process => process.processId),
          lineItemType,
        };
        // Call API
        await createLineItem(createRequest);
        // Show success toast notification
        message.success('Line item added successfully!');
        // Reset insert position after successful add
        setInsertAtIndex(null);
      } else {
        // Update existing item
        const updateRequest: UpdateLineItemRequest = {
          statementLineItemId: selectedItem?.id,
          financialStatementId,
          name: values.name,
          amount: values.amount,
          isImmaterial: values.isImmaterial,
          cycleIds: values.cycles.map(cycle => cycle.cycleId),
          processIds: values.processes.map(process => process.processId),
          lineItemType,
        };
        // Call API
        await updateLineItem(updateRequest);
        // Show success toast notification
        message.success('Line item updated successfully!');
        setSelectedItem(undefined);
      } 

      reloadSelectedSection();
      
    } catch (error) {
      message.error('Failed to save line item');
      throw error; // Re-throw to let drawer handle loading state
    }
  };

  const reloadSelectedSection = async () => {
    try {
      const request = {
        requiresCounts: true,
        skip: 0,
        take: 1000, // Get all items (adjust if needed)
      };
      if (!financialStatementId) {
        message.error('Financial statement ID not found');
        return;
      }
      switch (currentSection) {
        case 'assets': {
          const res = await getAssets(financialStatementId, request);
          const updatedData = res.result.map(parseBackendLineItem);
          dispatch(setAllFinancialData({
            assetsData: updatedData,
            liabilitiesData,
            equityData,
            revenueData,
            expensesData,
            reportId: financialStatementId,
          }));
          break;
        }
        case 'liabilities': {
          const res = await getLiabilities(financialStatementId, request);
          const updatedData = res.result.map(parseBackendLineItem);
          dispatch(setAllFinancialData({
            assetsData,
            liabilitiesData: updatedData,
            equityData,
            revenueData,
            expensesData,
            reportId: financialStatementId,
          }));
          break;
        }
        case 'equity': {
          const res = await getEquities(financialStatementId, request);
          const updatedData = res.result.map(parseBackendLineItem);
          dispatch(setAllFinancialData({
            assetsData,
            liabilitiesData,
            equityData: updatedData,
            revenueData,
            expensesData,
            reportId: financialStatementId,
          }));
          break;
        }
        case 'revenue': {
          const res = await getRevenues(financialStatementId, request);
          const updatedData = res.result.map(parseBackendLineItem);
          dispatch(setAllFinancialData({
            assetsData,
            liabilitiesData,
            equityData,
            revenueData: updatedData,
            expensesData,
            reportId: financialStatementId,
          }));
          break;
        }
        case 'expenses': {
          const res = await getExpenses(financialStatementId, request);
          const updatedData = res.result.map(parseBackendLineItem);
          dispatch(setAllFinancialData({
            assetsData,
            liabilitiesData,
            equityData,
            revenueData,
            expensesData: updatedData,
            reportId: financialStatementId,
          }));
          break;
        }
      }
  } catch (error) {
    message.error('Failed to reload selected section');
    throw error;
  }
  }
  // Export columns configuration
  const exportColumns: ExportColumnConfig[] = useMemo(() => [
    { key: 'section', title: 'Section', width: 15 },
    { key: 'name', title: 'Line Item', width: 30 },
    { key: 'amount', title: 'Amount', width: 15 },
    { key: 'cycles', title: 'Cycles', width: 25 },
    { key: 'processes', title: 'Processes', width: 25 },
    { key: 'isImmaterial', title: 'Immaterial', width: 12 },
  ], []);

  // Prepare export data based on active tab
  const exportData = useMemo(() => {
    const formatCycles = (cycles: any[]) => 
      cycles?.map(c => `${c.cycleCode} ${c.cycleName}`).join(', ') || '';
    const formatProcesses = (processes: any[]) => 
      processes?.map(p => `${p.processCode} ${p.processName}`).join(', ') || '';

    if (activeTab === 'balance-sheet') {
      return [
        ...assetsData.map(item => ({
          section: 'Assets',
          name: item.name,
          amount: item.amount,
          cycles: formatCycles(item.cycles),
          processes: formatProcesses(item.processes),
          isImmaterial: item.isImmaterial ? 'Yes' : 'No',
        })),
        ...liabilitiesData.map(item => ({
          section: 'Liabilities',
          name: item.name,
          amount: item.amount,
          cycles: formatCycles(item.cycles),
          processes: formatProcesses(item.processes),
          isImmaterial: item.isImmaterial ? 'Yes' : 'No',
        })),
        ...equityData.map(item => ({
          section: 'Equity',
          name: item.name,
          amount: item.amount,
          cycles: formatCycles(item.cycles),
          processes: formatProcesses(item.processes),
          isImmaterial: item.isImmaterial ? 'Yes' : 'No',
        })),
      ];
    } else {
      return [
        ...revenueData.map(item => ({
          section: 'Revenue',
          name: item.name,
          amount: item.amount,
          cycles: formatCycles(item.cycles),
          processes: formatProcesses(item.processes),
          isImmaterial: item.isImmaterial ? 'Yes' : 'No',
        })),
        ...expensesData.map(item => ({
          section: 'Expenses',
          name: item.name,
          amount: item.amount,
          cycles: formatCycles(item.cycles),
          processes: formatProcesses(item.processes),
          isImmaterial: item.isImmaterial ? 'Yes' : 'No',
        })),
      ];
    }
  }, [activeTab, assetsData, liabilitiesData, equityData, revenueData, expensesData]);

  // Export file name based on active tab
  const exportFileName = activeTab === 'balance-sheet' 
    ? 'BalanceSheet_FinancialStatements' 
    : 'IncomeStatement_FinancialStatements';

  // Tooltip styling for materiality threshold
  const tooltipStyle = {
    maxWidth: '256px',
  };

  const tooltipInnerStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    color: '#1F2937',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '120%',
    letterSpacing: '1%',
    textAlign: 'center' as const,
    padding: '12px 16px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
  };

  const balanceSheetContent = (
    <>
      <MaterialityThresholdSection>
        <ThresholdLabel>
          Enter materiality threshold
          <Tooltip
            title={materialityTooltipText}
            placement="top"
            overlayStyle={tooltipStyle}
            overlayInnerStyle={tooltipInnerStyle}
            color="#FFFFFF"
          >
            <InfoIcon />
          </Tooltip>
        </ThresholdLabel>
        <ThresholdInput
          prefix="$"
          placeholder="Write Materiality"
          value={materialityThreshold}
          onChange={(e) => handleMaterialityChange(e.target.value)}
          onKeyDown={handleMaterialityKeyPress}
          disabled={!isAdmin}
        />
      </MaterialityThresholdSection>

      {hasInvalidLineItems && (
        <ErrorBanner
          message="Error: One or more line items have an invalid audit configuration. Please ensure all items are linked to both a Cycle and a Process, or are marked Immaterial."
          type="warning"
        // showIcon
        />
      )}

      <AccountSectionComponent
        title="Assets"
        bgColor="#E2EBFF"
        data={assetsData}
        onAddItem={() => handleAddItem('assets')}
        onEditItem={(item) => handleEditItem(item, 'assets')}
        onDeleteItem={(item) => handleDeleteItem(item, 'assets')}
        onAddRowAbove={(item) => handleAddRowAbove(item, 'assets')}
        onAddRowBelow={(item) => handleAddRowBelow(item, 'assets')}
      />

      <AccountSectionComponent
        title="Liabilities"
        bgColor="#E2EBFF"
        data={liabilitiesData}
        onAddItem={() => handleAddItem('liabilities')}
        onEditItem={(item) => handleEditItem(item, 'liabilities')}
        onDeleteItem={(item) => handleDeleteItem(item, 'liabilities')}
        onAddRowAbove={(item) => handleAddRowAbove(item, 'liabilities')}
        onAddRowBelow={(item) => handleAddRowBelow(item, 'liabilities')}
      />

      <AccountSectionComponent
        title="Equity"
        bgColor="#E2EBFF"
        data={equityData}
        onAddItem={() => handleAddItem('equity')}
        onEditItem={(item) => handleEditItem(item, 'equity')}
        onDeleteItem={(item) => handleDeleteItem(item, 'equity')}
        onAddRowAbove={(item) => handleAddRowAbove(item, 'equity')}
        onAddRowBelow={(item) => handleAddRowBelow(item, 'equity')}
      />

      {hasReconciliationError && !isReconciliationBannerDismissed && (
        <ReconciliationErrorBanner
          message={'"Total Assets" minus "Total Liabilities" does not equal "Equity"'}
          type="warning"
          closable
          onClose={handleDismissReconciliationBanner}
        />
      )}
    </>
  );

  const incomeStatementContent = (
    <>
      <MaterialityThresholdSection>
        <ThresholdLabel>
          Enter materiality threshold
          <Tooltip
            title={materialityTooltipText}
            placement="top"
            overlayStyle={tooltipStyle}
            overlayInnerStyle={tooltipInnerStyle}
            color="#FFFFFF"
          >
            <InfoIcon />
          </Tooltip>
        </ThresholdLabel>
        <ThresholdInput
          prefix="$"
          placeholder="Write Materiality"
          value={materialityThreshold}
          onChange={(e) => handleMaterialityChange(e.target.value)}
          onKeyDown={handleMaterialityKeyPress}
          disabled={!isAdmin}
        />
      </MaterialityThresholdSection>

      {hasInvalidLineItems && (
        <ErrorBanner
          message="Error: One or more line items have an invalid audit configuration. Please ensure all items are linked to both a Cycle and a Process, or are marked Immaterial."
          type="warning"
          // showIcon
        />
      )}

      <AccountSectionComponent
        title="Revenue"
        bgColor="#E2EBFF"
        data={revenueData}
        onAddItem={() => handleAddItem('revenue')}
        onEditItem={(item) => handleEditItem(item, 'revenue')}
        onDeleteItem={(item) => handleDeleteItem(item, 'revenue')}
        onAddRowAbove={(item) => handleAddRowAbove(item, 'revenue')}
        onAddRowBelow={(item) => handleAddRowBelow(item, 'revenue')}
      />

      <AccountSectionComponent
        title="Expenses"
        bgColor="#E2EBFF"
        data={expensesData}
        onAddItem={() => handleAddItem('expenses')}
        onEditItem={(item) => handleEditItem(item, 'expenses')}
        onDeleteItem={(item) => handleDeleteItem(item, 'expenses')}
        onAddRowAbove={(item) => handleAddRowAbove(item, 'expenses')}
        onAddRowBelow={(item) => handleAddRowBelow(item, 'expenses')}
      />

      {(() => {
        const totalRevenue = calculateSectionTotal(revenueData);
        const totalExpenses = calculateSectionTotal(expensesData);
        const netIncome = totalRevenue - totalExpenses;
        const formattedNetIncome = netIncome < 0
          ? `$(${Math.abs(netIncome).toLocaleString()})`
          : `$${netIncome.toLocaleString()}`;
        return (
          <GridWrapper>
            <NetIncomeRow>
              <NetIncomeLabel>Net Income / (Loss):</NetIncomeLabel>
              <NetIncomeValue>{formattedNetIncome}</NetIncomeValue>
            </NetIncomeRow>
          </GridWrapper>
        );
      })()}
    </>
  );

  const tabItems = [
    {
      key: 'balance-sheet',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Balance Sheet
        </span>
      ),
      children: balanceSheetContent,
    },
    {
      key: 'income-statement',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Income statement
        </span>
      ),
      children: incomeStatementContent,
    },
  ];

  return (
    <div>
      <DropdownMenuStyles />

      <PageHeader>
        <PageTitle level={2}>ICFR Process Mapping to Financial Statements</PageTitle>
        <HeaderActions>
          <ExportButton onClick={() => setExportPanelOpen(true)} />
          {selectedReport && isAdmin && (
            <ChangeLogButton
              actionType="financialStatement"
              annualReportId={selectedReport.annualReportId}
            />
          )}
        </HeaderActions>
      </PageHeader>

      <ContentWrapper>
        <TabsContainer>
          <StyledTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />

          {isLoadingData && (
            <LoadingOverlay>
              <LoadingSpinner>
                <div className="spinner" />
                <div className="text">Loading financial statement data...</div>
              </LoadingSpinner>
            </LoadingOverlay>
          )}
        </TabsContainer>
      </ContentWrapper>

      {/* Financial Statement Item Drawer */}
      {selectedReport && (
        <FinancialStatementItemDrawer
          open={drawerOpen}
          mode={drawerMode}
          initialValues={selectedItem}
          annualReportId={selectedReport.annualReportId}
          onClose={handleCloseDrawer}
          onSubmit={handleSubmitForm}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        icon={warningIcon}
        title="Delete line item"
        description="Are you sure you want to delete this line item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={isDeleting}
      />

      {/* Export Panel */}
      <ExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
        data={exportData}
        columns={exportColumns}
        fileName={exportFileName}
        title={activeTab === 'balance-sheet' ? 'ICFR Process Mapping to Balance Sheet' : 'ICFR Process Mapping to Income Statement'}
      />
    </div>
  );
};

export default FinancialStatementsPage;

